/**
 * The unguided task evaluator.
 *
 * Pure on purpose: React knows when to re-run this, but it should not be needed
 * to decide what a task means. Everything here is a function of a plain facts
 * object, so the interesting cases — a task that must not tick early, a task
 * whose evidence has since been destroyed — are unit-testable without a DOM.
 */
import type { Milestone } from '@/player/sim-progress'
import type { Task, TaskCheck } from '@/schema'
import {
  selectClassTempStatus,
  selectDistinctSkuCount,
  selectHasHeldQpo,
  selectHasPartialSelection,
  selectHasShortfall,
  selectHasSku,
  selectQpoStatus,
  selectQpoSupplierDetailsComplete,
  selectQpoSupplierSet,
} from '@/screens/ordering/store'
import type { CartLine, QpoSession } from '@/screens/ordering/types'

/** Everything a check is allowed to look at. */
export interface SimFacts {
  lines: CartLine[]
  qpo: QpoSession
  heldQpos: QpoSession[]
  visitedRoutes: string[]
  milestones: Milestone[]
}

/**
 * Does a `route-visited` pattern match a path the learner actually reached?
 *
 * A bare path is an exact match. A `:param` segment matches any single
 * segment, so content can say `/history/:id` and mean "opened some delivery
 * record" rather than naming one the learner may never pick.
 */
export function routeMatches(pattern: string, visited: string): boolean {
  const want = pattern.split('/').filter(Boolean)
  const got = visited.split('/').filter(Boolean)
  if (want.length !== got.length) return false
  return want.every((seg, i) => seg.startsWith(':') || seg === got[i])
}

/**
 * Does one check hold right now?
 *
 * Exhaustive over `TaskCheck['kind']`: with `noFallthroughCasesInSwitch` and a
 * closed Zod union, adding a check kind to the schema without handling it here
 * fails the type check rather than silently returning false.
 */
export function checkHolds(check: TaskCheck, facts: SimFacts): boolean {
  switch (check.kind) {
    case 'cart-has-sku':
      return selectHasSku(facts, check.skuId)
    case 'cart-distinct-skus':
      return selectDistinctSkuCount(facts) >= check.atLeast
    case 'cart-partial-selection':
      return selectHasPartialSelection(facts)
    case 'route-visited':
      return facts.visitedRoutes.some((visited) => routeMatches(check.path, visited))
    case 'qpo-supplier-set':
      return selectQpoSupplierSet(facts)
    case 'qpo-supplier-details-complete':
      return selectQpoSupplierDetailsComplete(facts)
    case 'qpo-class-temp':
      return selectClassTempStatus(facts, check.category, check.status)
    case 'qpo-status':
      return selectQpoStatus(facts, check.status)
    case 'qpo-held':
      return selectHasHeldQpo(facts)
    case 'qpo-has-shortfall':
      return selectHasShortfall(facts)
    case 'milestone':
      return facts.milestones.includes(check.name)
  }
}

/** A task is earned when every one of its checks holds. */
export function taskHolds(task: Task, facts: SimFacts): boolean {
  return task.checks.length > 0 && task.checks.every((check) => checkHolds(check, facts))
}

/**
 * Fold the current facts into the set of already-earned task ids.
 *
 * One rule that the naive "recompute every task from scratch" version gets
 * wrong:
 *
 * - **Monotonic.** Once earned, a task stays earned. Required because the app
 *   destroys evidence: `clear()` on order submit, `resetQpo()` on print and on
 *   the Articlepedia vendor screen's mount effect.
 *
 * Tasks tick whenever their checks hold, regardless of `after` ordering. The
 * `after` field is used only by the click-blocker in `TaskOverlay` to restrict
 * navigation while a task's prerequisite is pending — it does not gate ticking.
 * Enforcing tick order caused out-of-sequence completions to be permanently
 * missed: e.g. a user who scans a QPO supplier before visiting My Orders would
 * find that task never ticks, because visiting My Orders calls `resetQpo()` and
 * destroys the evidence before the task is unblocked.
 *
 * Returns the ids in task-definition order so the checklist renders stably.
 */
export function evaluateTasks(tasks: Task[], facts: SimFacts, earned: readonly string[]): string[] {
  const done = new Set(earned)
  for (const task of tasks) {
    if (done.has(task.id)) continue
    if (taskHolds(task, facts)) done.add(task.id)
  }
  return tasks.filter((t) => done.has(t.id)).map((t) => t.id)
}
